import { Component, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, distinctUntilChanged } from 'rxjs/operators';
import { shallowCompare } from '@ipreo/muni-shared';
import { CupcakePopoverOptions } from '@ipreo/cupcake-components';
import { GameDayRootReducerState } from '../../../state/root.state';
import {
  CombineTranche,
  CombineAllTranches
} from '../../../state/charts/actions';
import {
  BarChartData,
  BarGroupData
} from '../../../shared/models/charts/chart-data-types';
import {
  getFullGamedayState,
  getGamedayDataState,
  getChartsState
} from '../../../state/data/reducer';
import { getChartData } from '../../mappers/bar-chart.mapper';
import { TrancheGroup } from '../../data/gameDay-data.transformations';
import { calculateData } from '../../data/gameDay-data.calculation';

@Component({
  selector: 'muni-gameday-combine-tranches',
  templateUrl: './combine-tranches.template.html',
  styleUrls: ['./combine-tranches.styles.scss'],
  standalone: false,
})
export class CombineTranchesComponent implements OnDestroy {
  public trancheIds: string[] = [];
  public tranchesCombined: boolean;

  public progressBarIcon: string;

  private readonly tranchesCombined$: Observable<boolean> = this.store.pipe(
    select(getChartsState),
    map(({ isAllTranchesCombined }) => isAllTranchesCombined)
  );

  private readonly chartData: Observable<BarChartData> = this.store.pipe(
    select(getFullGamedayState),
    filter(({ data: { deal } }) => !!deal),
    map(getChartData)
  );

  public readonly trancheGroupsIds: Observable<string[]> = this.store.pipe(
    select(getGamedayDataState),
    filter(({ deal }) => !!deal),
    map(({ deal, orders, filters: { viewType } }) =>
      calculateData({ deal, orders }, viewType)
    ),
    distinctUntilChanged(shallowCompare),
    map(calcData =>
      calcData.trancheGroups.reduce(
        (prev: string[], { id }: TrancheGroup) => [...prev, id],
        []
      )
    )
  );

  private readonly trancheGroups: Observable<
    BarGroupData[]
  > = this.chartData.pipe(
    map(({ groups }: BarChartData) =>
      groups.filter(
        ({ barsCount, combined }: BarGroupData) =>
          (barsCount === 1 && combined) || barsCount > 1
      )
    )
  );

  public readonly showCombineButton: Observable<
    boolean
  > = this.trancheGroups.pipe(
    map(groups =>
      groups.some(
        ({ barsCount, combined }: BarGroupData) => barsCount > 1 || combined
      )
    )
  );

  private readonly trancheGroupsIdsSubscription: Subscription = this.trancheGroupsIds.subscribe(
    (data: string[]) => (this.trancheIds = data)
  );

  private readonly tranchesCombinedSubscription: Subscription = this.tranchesCombined$.subscribe(
    (data: boolean) => (this.tranchesCombined = data)
  );

  constructor(private store: Store<GameDayRootReducerState>) {}

  public get popover() {
    return new CupcakePopoverOptions({
      trigger: 'hover',
      text: `Click to ${
        this.tranchesCombined ? 'expand' : 'combine'
      } all tranches`,
      position: 'left'
    });
  }

  public ngOnDestroy(): void {
    this.trancheGroupsIdsSubscription.unsubscribe();
    this.tranchesCombinedSubscription.unsubscribe();
  }

  public trancheViewToggle(): void {
    const tranchesCombined = !this.tranchesCombined;
    this.store.dispatch(new CombineAllTranches(tranchesCombined));
    this.store.dispatch(
      new CombineTranche(tranchesCombined ? this.trancheIds : [])
    );
  }
}
